<template>
<router-view v-slot="{ Component }">
  <transition name="fade" mode="out-in">
    <component :is="Component" />
  </transition>
</router-view>
</template>

<script lang="ts" setup>
/**
 * Componente raíz de la aplicación.
 *
 * Se limita a montar el `router-view` con una transición de desvanecido entre
 * vistas. El layout real (menú lateral y barra superior) vive en
 * `PaginaPrincipal`, que es la ruta padre de la que cuelgan casi todas las demás.
 *
 * La transición usa `mode="out-in"` para que la vista saliente termine de
 * desaparecer antes de que entre la nueva; sin eso, ambas coexisten un instante y
 * el layout salta.
 */
</script>


<style>

:root {
  /* --- Colores primarios --- */
  --color-primario: #0e69a0;
  --color-secundario: #eea222;
  --color-login-accent: #FDCB0C;

  /* --- Colores de texto --- */
  --text-primary: #1a1a1a;
  --text-secondary: #555;
  --text-tertiary: #666;
  --text-muted: #8f8e8e;
  --text-disabled: #999;

  /* --- Colores de fondo --- */
  --bg-white: #fff;
  --bg-light: #fafafa;
  --bg-input: #f9f9f9;
  --bg-section: #f5f5f5;
  --bg-container: rgb(220, 223, 224);

  /* --- Bordes --- */
  --border-light: #e6e5e5;
  --border-medium: #ccc;
  --border-separator: #e0e0e0;

  /* --- Semánticos --- */
  --color-success: #28a745;
  --color-success-bg: #d4edda;
  --color-success-text: #155724;
  --color-error: #dc3545;
  --color-error-bg: #f8d7da;
  --color-error-text: #721c24;
  --color-warning-bg: #fff3cd;
  --color-warning-text: #856404;

  /* --- Sombras --- */
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.04);
  --shadow-lg: 0 10px 20px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04);

  /* --- Border radius --- */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-pill: 20px;

  /* --- Espaciado --- */
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 12px;
  --space-base: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
  --space-2xl: 48px;

  /* --- Tipografía --- */
  --font-family: 'Roboto', sans-serif;
  --font-size-xs: 0.75rem;
  --font-size-sm: 0.85rem;
  --font-size-base: 0.95rem;
  --font-size-md: 1.05rem;
  --font-size-lg: 1.2rem;
  --font-size-xl: 1.5rem;
  --font-size-2xl: 1.8rem;
  --line-height-tight: 1.3;
  --line-height-normal: 1.5;
  --line-height-relaxed: 1.6;

  /* --- Transiciones --- */
  --transition-fast: 0.15s ease;
  --transition-normal: 0.2s ease;
  --transition-slow: 0.3s ease;
}


*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Mejora global de focus para accesibilidad */
:focus-visible {
  outline: 2px solid var(--color-primario);
  outline-offset: 2px;
}

/* Mejora global de selección de texto */
::selection {
  background: rgba(14, 105, 160, 0.15);
  color: inherit;
}

/* Transiciones de página */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-normal);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Responsive portátiles pequeños (≤1280px) ─────────────────────────────
   Corrige los patrones más repetidos sin necesidad de tocar cada componente.
   Los !important son intencionales: anulan las reglas scoped de cada vista.
────────────────────────────────────────────────────────────────────────── */
@media (max-width: 1280px) {
  /* Encabezados: los botones bajan si no caben en la fila */
  .header-flex,
  .encabezado-flex {
    flex-wrap: wrap !important;
    gap: 0.6rem !important;
  }

  /* Filas de acciones */
  .acciones-header,
  .acciones-encabezado,
  .botones {
    flex-wrap: wrap !important;
    gap: 0.4rem !important;
  }

  /* Botones: que no fuerzen un ancho mínimo que no cabe */
  .btn {
    min-width: 0 !important;
    white-space: normal !important;
    text-align: center !important;
  }

  /* Filtros y chips de tipo que también se usan como filas flex */
  .filtros-tipo,
  .filtro-botones {
    flex-wrap: wrap !important;
  }

  /* Nombres alternativos de fila de acciones usados en algunos componentes */
  .header-acciones,
  .encabezado-acciones {
    flex-wrap: wrap !important;
    gap: 0.4rem !important;
  }

  /* Tablas: el contenedor más común es .marco; con overflow-x scrollea en vez de romper */
  .marco {
    overflow-x: auto;
  }
}

/* Pantallas muy pequeñas de portátil (≤1100px): reducir padding de botones */
@media (max-width: 1100px) {
  .btn {
    padding: 0.45rem 0.75rem !important;
    font-size: 0.8rem !important;
    height: auto !important;
    line-height: 1.3 !important;
  }

  /* Inputs que tienen un min-width fijo generoso */
  input[type="text"],
  input[type="number"],
  input[type="date"],
  input[type="email"],
  input[type="url"],
  input[type="search"],
  select,
  textarea {
    min-width: 0 !important;
    max-width: 100% !important;
  }
}

/* --- Responsive: pantallas grandes (QHD 2560px+) --- */
@media (min-width: 2560px) {
  html {
    font-size: 20px;
  }
  :root {
    --space-xs: 6px;
    --space-sm: 10px;
    --space-md: 16px;
    --space-base: 20px;
    --space-lg: 30px;
    --space-xl: 40px;
    --space-2xl: 60px;
  }
}

/* --- Responsive: pantallas 4K (3840px+) --- */
@media (min-width: 3840px) {
  html {
    font-size: 26px;
  }
  :root {
    --space-xs: 8px;
    --space-sm: 14px;
    --space-md: 22px;
    --space-base: 28px;
    --space-lg: 42px;
    --space-xl: 56px;
    --space-2xl: 84px;
  }
}
</style>
